// Sprint-V25 Issue L (Sa 12.09.2026) — HeroHookSection · Russell-Brunson-Magic
// Layout-Refactor auf Andreas-Direktive:
//   „Wir brauchen etwas Magisches. Orientiert euch an Russell Brunson
//    Landingpages — aber im Steve-Jobs-Reduktions-Ton."
// Aufbau (mobile-first 375px, skaliert bis Desktop):
//   1. Kicker-Pill („Der Fokustypen-Test") - klein, Uppercase, warm-gold
//   2. Riesige Pain-Headline (2 Zeilen, variiert A/B/C/D)
//   3. Konstanter Anker-Satz: "Absicht ist nicht Wirkung." mit Gold-Underline
//      auf „ändern" - die Hoffnungs-Diagnose in einem Rutsch
//   4. Vier Tier-Icons als visuelle Row (Hund · Katze · Biene · Eule) mit
//      Mini-Mottos - der Curiosity-Hook „welches bin ich?"
//   5. Big-CTA (Full-Width Mobile, Auto Desktop, hover-lift)
//   6. Reassurance-Row „5 Min · Kein Account · Sofort-Ergebnis"

import React from "react";
import { Clock, Sparkles, Eye } from "lucide-react";
import { HOOK_COPY, HOOK_ANCHOR, FOKUSTYPEN_SEQUENCE, CTA_LABEL, HEADLINE_CANDIDATES } from "@/data/landingHookCopy";
import { isPreviewRuntime, getVisitSource } from "@/lib/trafficSource";
import iconHund from "@/assets/tier-icons/hund.png";
import iconKatze from "@/assets/tier-icons/katze.png";
import iconBiene from "@/assets/tier-icons/biene.png";
import iconEule from "@/assets/tier-icons/eule.png";

// Sprint-V25 Issue L Hotfix (Sa 12.09.2026 spaeter) — Andreas-Direktive:
// Statt Lucide-Icons die vom User bereitgestellten Marken-Icons benutzen
// (Hund/Katze/Biene/Eule aus /assets/tier-icons/*).
const TIER_ICON_SRC = {
  hund: iconHund,
  katze: iconKatze,
  biene: iconBiene,
  eule: iconEule,
};

export default function HeroHookSection({ variant = "a", onCtaClick, onLoginClick }) {
  const copy = HOOK_COPY[variant] || (isPreviewRuntime() && HEADLINE_CANDIDATES[variant]) || HOOK_COPY.a;
  return (
    <section
      data-testid="lp-hero-section"
      data-hook-variant={variant}
      data-traffic-source={isPreviewRuntime() ? getVisitSource().source : undefined}
      className="relative w-full overflow-hidden bg-[#FAF7F2] dark:bg-slate-950"
    >
      {/* Subtle radial glow — Russell-Brunson-Magic ohne Farbtoene */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-70 dark:opacity-30"
        style={{
          background:
            "radial-gradient(ellipse 60% 40% at 50% 0%, rgba(220, 38, 38, 0.06), transparent 70%)",
        }}
      />

      <div className="relative mx-auto w-full max-w-md sm:max-w-5xl lg:max-w-6xl px-6 sm:px-10 pt-10 pb-14 sm:pt-24 sm:pb-28 flex flex-col items-center text-center">
        {/* 1. Kicker-Pill */}
        <span
          data-testid="lp-hero-kicker"
          className="inline-flex items-center gap-2 rounded-full bg-white dark:bg-slate-900 border border-amber-300/70 dark:border-amber-700/50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.15em] text-amber-700 dark:text-amber-400 shadow-sm"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" aria-hidden="true" />
          Der Fokustypen-Test
        </span>

        {/* 2. Riesige Pain-Headline
            Sprint-V25 Issue L Hotfix (Sa 12.09.2026 spaet) — Andreas-Direktive:
            Desktop-Viewport voll nutzen, damit die zweizeilige Headline nicht
            zu 4 Zeilen umbricht. Auf Desktop keine char-Begrenzung, dafuer
            groessere Type (72px) fuer Russell-Brunson-Wirkung. */}
        <h1
          data-testid="lp-hero-headline"
          className="mt-6 sm:mt-10 font-extrabold tracking-tight text-slate-900 dark:text-slate-50 text-[34px] leading-[1.1] sm:text-[56px] sm:leading-[1.05] lg:text-[68px] max-w-[18ch] sm:max-w-none"
        >
          {copy.headline.map((line, i) => (
            <span key={i} className="block">
              {line}
            </span>
          ))}
        </h1>

        {/* 3. Konstanter Anker — Diagnose + Hoffnung */}
        <p
          data-testid="lp-hero-anchor"
          className="mt-6 sm:mt-10 text-[18px] sm:text-[24px] leading-[1.4] text-slate-600 dark:text-slate-300 font-normal max-w-[26ch] sm:max-w-none"
        >
          <span className="block">{HOOK_ANCHOR.first}</span>
          <span className="block">
            {"Das kannst du "}
            <span className="relative inline-block font-semibold text-slate-900 dark:text-white">
              ändern
              <span
                aria-hidden="true"
                className="absolute left-0 right-0 -bottom-0.5 h-[3px] rounded-full bg-amber-400"
              />
            </span>
            .
          </span>
        </p>

        {/* 4. Vier Tier-Icons */}
        <div
          data-testid="lp-hero-fokustypen"
          className="mt-10 sm:mt-16 grid grid-cols-4 gap-3 sm:gap-8 w-full max-w-xs sm:max-w-2xl"
        >
          {FOKUSTYPEN_SEQUENCE.map((t) => {
            const iconSrc = TIER_ICON_SRC[t.key];
            return (
              <div key={t.key} className="flex flex-col items-center gap-2 sm:gap-3" data-testid={`lp-hero-tier-${t.key}`}>
                <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-2xl sm:rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-sm flex items-center justify-center">
                  <img
                    src={iconSrc}
                    alt={t.label}
                    loading="lazy"
                    className="w-9 h-9 sm:w-12 sm:h-12 object-contain"
                  />
                </div>
                <span className="text-[12px] sm:text-base font-semibold text-slate-700 dark:text-slate-300">
                  {t.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* 5. Big-CTA */}
        <button
          type="button"
          data-testid="lp-hero-cta"
          onClick={() => onCtaClick && onCtaClick("hero")}
          className="mt-10 sm:mt-16 w-full sm:w-auto min-h-[56px] sm:min-h-[64px] px-8 sm:px-12 rounded-2xl font-bold text-[18px] sm:text-[20px] text-white bg-[var(--fts-accent,#DC2626)] hover:bg-[var(--fts-accent-hover,#B91C1C)] shadow-[0_10px_30px_-8px_rgba(220,38,38,0.5)] hover:shadow-[0_16px_40px_-8px_rgba(220,38,38,0.55)] hover:-translate-y-0.5 transition-all focus:outline-none focus-visible:ring-4 focus-visible:ring-red-200 dark:focus-visible:ring-red-900/40"
        >
          {CTA_LABEL}
        </button>

        {/* 6. Reassurance-Row
            Sprint-V25 Issue L Hotfix (Sa 12.09.2026 09:15) — Andreas-Direktive:
            Statt „Kein Account" (Falschversprechen, weil danach Magic-Link
            angefordert wird) → ehrliche Benefit-Sprache. */}
        <div
          data-testid="lp-hero-reassurance"
          className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[13px] sm:text-sm text-slate-500 dark:text-slate-400"
        >
          <span className="inline-flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" aria-hidden="true" />
            5 Minuten
          </span>
          <span className="text-slate-300 dark:text-slate-600" aria-hidden="true">
            ·
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Eye className="w-3.5 h-3.5" aria-hidden="true" />
            Ehrlicher Spiegel
          </span>
          <span className="text-slate-300 dark:text-slate-600" aria-hidden="true">
            ·
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
            Erster Aha-Moment
          </span>
        </div>

        {/* 7. Sekundaerer Login-CTA fuer rueckkehrende User
            Sprint-V25 Issue L Hotfix (Sa 12.09.2026 09:25) — Andreas-Direktive:
            „Als aktiver User muss ich hier auch anmelden koennen." */}
        {onLoginClick && (
          <button
            type="button"
            data-testid="lp-hero-login-secondary"
            onClick={onLoginClick}
            className="mt-8 text-sm sm:text-base text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
          >
            Schon dabei?{" "}
            <span className="font-semibold underline decoration-slate-300 dark:decoration-slate-600 underline-offset-4 hover:decoration-slate-700 dark:hover:decoration-slate-300">
              Hier anmelden →
            </span>
          </button>
        )}
      </div>
    </section>
  );
}
